import { useState, useRef, useEffect } from "react";

export interface MultipleSelectOption {
  value: string;
  label: string;
}

interface MultipleSelectProps {
  options: MultipleSelectOption[];
  defaultValue?: string[];
  onChange?: (selectedValues: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function MultipleSelect({
  options,
  defaultValue = [],
  onChange,
  placeholder = "Select options...",
  className = "",
  disabled = false,
}: MultipleSelectProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected options for display
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value)
  );

  // Handle option selection
  const handleSelect = (optionValue: string, e?: React.MouseEvent | React.ChangeEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    const newSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newSelectedValues);
    onChange?.(newSelectedValues);
    setSearchTerm("");
  };

  // Handle removing a selected item
  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelectedValues = selectedValues.filter((v) => v !== optionValue);
    setSelectedValues(newSelectedValues);
    onChange?.(newSelectedValues);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  // Handle input click
  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };

  // Handle keydown events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && searchTerm === "" && selectedValues.length > 0) {
      // Remove last selected item on backspace
      const newSelectedValues = selectedValues.slice(0, -1);
      setSelectedValues(newSelectedValues);
      onChange?.(newSelectedValues);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Hidden native select for form compatibility */}
      <select multiple className="hidden" defaultValue={defaultValue}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Custom select input */}
      <div
        className={`w-full p-4 rounded-md bg-White border-2 border-Grey4 min-h-[3rem] flex flex-wrap items-center gap-2 cursor-text ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${isOpen ? "border-PrimaryGreen" : ""}`}
        onClick={handleInputClick}
      >
        {/* Selected items as chips */}
        {selectedOptions.map((option) => (
          <span
            key={option.value}
            className="inline-flex items-center gap-1 px-2 py-1 bg-Grey4 rounded text-sm"
          >
            <span>{option.label}</span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => handleRemove(option.value, e)}
                className="hover:bg-Grey3 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${option.label}`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </span>
        ))}

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={selectedValues.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] outline-none bg-transparent"
          disabled={disabled}
        />

        {/* Dropdown arrow */}
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen);
              inputRef.current?.focus();
            }
          }}
          className="ml-auto text-Grey4 hover:text-PrimaryGreen transition-colors"
          disabled={disabled}
        >
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div 
          className="absolute z-50 w-full mt-1 bg-White border-2 border-Grey4 rounded-md shadow-lg max-h-60 overflow-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {filteredOptions.length === 0 ? (
            <div className="p-4 text-Grey4 text-center">No options found</div>
          ) : (
            <ul className="py-1">
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <li
                    key={option.value}
                    className={`px-4 py-2 cursor-pointer hover:bg-Grey4 transition-colors ${
                      isSelected ? "bg-Grey4" : ""
                    }`}
                    onClick={(e) => handleSelect(option.value, e)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelect(option.value, e);
                        }}
                        className="w-4 h-4 text-PrimaryGreen border-Grey4 rounded focus:ring-PrimaryGreen"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className={isSelected ? "font-medium" : ""}>
                        {option.label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}